import * as AvrMath from './Math';
export const requiredChannels = ['cumulative', 'x', 'y', 'z'];
export const defaultChannels = ['time', 'x', 'y', 'z', 'cumulative', 'height', 'depth'];
const kDefaultTrackLength = 16000;
/*
 * A Channel is a vector of a single property of a track, such as time or
 * distance. This class provides getters to commonly needed attributes, such
 * as the first and last values, BUT DOES NOT PROVIDE ACCESS TO THE DATA ITSELF.
 * You must go through Track to sample values from the channel.
 */
class Channel {
    constructor(track, name) {
        this._track = undefined;
        this._name = undefined;
        this._track = track;
        this._name = name;
    }
    get first() {
        return this._track.length ? this._track._points[this._name][0] : undefined;
    }
    get last() {
        return this._track.length ? this._track._points[this._name][this._track.length - 1] : undefined;
    }
}
/*
 * Represents a set of points. As points are streamed in, they may be appended.
 * When points are added, the Track is marked as dirty for the remainder of the frame.
 * The only way to pull data out of a Track is to query it via the sample*() methods.
 * Internally we are then free to use a fast implementation, such as typed arrays.
 *
 * The SOA pattern is used so that sample queries are as fast as possible. E.g. querying
 * a time only has to fetch time doubles from a single linear typed array. This is a win
 * because we're doing much more work searching than we are constructing full point objects
 *
 * @NOTE: This class is not exported for a reason. Only create a new Track though
 *        the TrackCollection.addTrack() method.
 * @TODO: Collect aggregate stats such as max and min height during creation
 */
class Track {
    constructor(id, channels = defaultChannels, expectedPointCount = kDefaultTrackLength) {
        this._id = '';
        this._newPointsOffset = undefined;
        this._channels = [];
        // Points in SOA format
        this._pointCount = 0;
        this._points = {};
        if (!id) {
            throw new Error('Track must have a valid id');
        }
        if (!expectedPointCount) {
            throw new Error('Track must specify a non-zero expected point count');
        }
        // Verify that the channels list contains all of the required channels
        requiredChannels.forEach(c => {
            if (!channels.includes(c)) {
                throw new Error(`Track does not include required channel "${c}"`);
            }
        });
        // Verify that the channels list contains no duplicates
        const unique = {};
        channels.forEach(c => {
            if (unique[c]) {
                throw new Error('Track has duplicate channels');
            }
            unique[c] = true;
        });
        this._channels = channels;
        this._id = String(id);
        this._expectedPointCount = expectedPointCount;
        channels.forEach(channelName => {
            this[channelName] = new Channel(this, channelName);
            // @HACK: We won't know the true type until points are first uploaded
            this._points[channelName] = new Float64Array(0);
        });
    }
    get id() {
        return this._id;
    }
    get dirty() {
        return this._newPointsOffset !== undefined;
    }
    get length() {
        return this._pointCount;
    }
    hasChannel(channel) {
        return this[channel] !== undefined;
    }
    isChannelSorted(property) {
        return property === 'cumulative' || property === 'time';
    }
    /*
     * Add points to the track. Any points that overlap in time with the existing points will not be added.
     * I.e. only "new" points will be appended. Returns the number of new points added.
     */
    appendPoints(points) {
        // If there are no new non-overlapping points, don't mark as dirty
        if (points.cumulative.byteLength === 0)
            return 0;
        // Set this offset so that consumers can query only new points
        // To handle multiple appends per frame, it is only set once
        // @NOTE: This will be cleared at the end of the next frame
        if (!this._newPointsOffset)
            this._newPointsOffset = this._pointCount;
        // Directly append each new ArrayBuffer to the existing channel buffers
        this._channels.forEach(channelName => {
            const vec = points[channelName];
            console.assert(vec, `Attempted to append Track that is missing channel ${channelName}`);
            this._appendChannel(channelName, vec);
        });
        const oldPointCount = this._pointCount;
        const newPointCount = this._points.cumulative.length;
        const addedCount = newPointCount - oldPointCount;
        this._pointCount = newPointCount;
        return addedCount;
    }
    clear() {
        this._pointCount = 0;
        Object.keys(this._points).forEach(key => {
            this._points[key] = new this._points[key].constructor();
        });
        this._newPointsOffset = undefined;
    }
    clearDirty() {
        // See appendPoints()
        this._newPointsOffset = undefined;
    }
    _appendChannel(channelName, vec) {
        // Get the type from the source typed array
        const vecType = vec.constructor;
        const channelView = this._points[channelName];
        let channelBuffer = channelView.buffer;
        // (Re)allocate space if the backing buffer is too small to hold the views
        if (channelView.byteLength + vec.byteLength > channelBuffer.byteLength) {
            // Allocate to the expected max point count, or double it if we're over
            const byteLength = channelBuffer.byteLength
                ? channelBuffer.byteLength * 2
                : this._expectedPointCount * vec.BYTES_PER_ELEMENT;
            channelBuffer = new ArrayBuffer(Math.max(byteLength, vec.byteLength));
            // Copy old data into the new buffer
            const tmpView = new vecType(channelBuffer);
            tmpView.set(channelView);
        }
        // Create a new larger view of our backing channel buffer and copy the new data in
        const newView = new vecType(channelBuffer, 0, channelView.length + vec.length);
        newView.set(vec, channelView.length);
        // Assign our new view
        this._points[channelName] = newView;
    }
    /*
     * Given a query object, this function seaches along the track for the two points on either side of the specified
     * search value. The properties of the 'result' object are filled with the linear interpolations of the properties
     * from the two points.
     *
     * Sample queries: { time: 30400000 }
     *                 { distance: 25.3 }
     */
    sampleLinear(query, result) {
        const key = Object.keys(query)[0];
        const val = query[key];
        const vec = this._points[key];
        const props = Object.keys(result);
        // Check query validity
        this._assertQueryValid(vec, key);
        if (vec.length <= 0 || Number.isNaN(val) || props.length === 0) {
            return undefined;
        }
        // Return the first or last point if out of bounds. Or undefined if we have no points.
        if (this._pointCount === 0) {
            return undefined;
        }
        else if (val <= vec[0]) {
            return this._buildPoint(0, result);
        }
        else if (val >= vec[vec.length - 1]) {
            return this._buildPoint(vec.length - 1, result);
        }
        // @TODO: Smarter search
        let idx = 0;
        for (; idx < this._pointCount; idx++) {
            if (vec[idx] > val) {
                break;
            }
        }
        // Lerp between the two points on either side
        const t = AvrMath.delerp(vec[idx - 1], vec[idx], val);
        for (let i = 0; i < props.length; i++) {
            const channel = this._points[props[i]];
            if (channel)
                result[props[i]] = AvrMath.lerp(channel[idx - 1], channel[idx], t);
            else if (props[i] === 'index')
                result.index = idx - 1 + t;
        }
        return result;
    }
    /* For a given query, return an array of the first point to satisfy each iteration of the query, until the end of the
     * track. E.g for a query distance of 5000 and a track with a distance of 27000, the result will contain the first
     * point, the first point whose distance surpasses 5000, the first point whose distance surpasses 10000, and then so
     * on until the final result which would be 25000.
     */
    sampleInterval(query, result) {
        const key = Object.keys(query)[0];
        const interval = query[key];
        const vec = this._points[key];
        const results = [];
        // Check query validity
        this._assertQueryValid(vec, key);
        if (Number.isNaN(interval) || !(interval > 0.0)) {
            throw new Error('sampleInterval query must be a positive, non-zero number, was', interval);
        }
        if (vec.length === 0) {
            return [];
        }
        // Find the first point that matches in each interval
        for (let i = 0; i < this._pointCount; i++) {
            const v = Math.floor(vec[i] / interval);
            if (results[v] === undefined) {
                results[v] = i;
            }
        }
        // Construct points for each of the indexes
        return results.map(idx => this._buildPoint(idx, Object.assign({}, result)));
    }
    /* Given a cartesian point, return the nearest cartesian point on the Track */
    findNearestPoint(point, result) {
        // @TODO: Use a distanceSqr function designed for typed arrays
        let minDist;
        let idx;
        const c = { x: 0.0, y: 0.0, z: 0.0 };
        for (let i = 0; i < this._pointCount; i++) {
            c.x = this._points.x[i];
            c.y = this._points.y[i];
            c.z = this._points.z[i];
            const d = AvrMath.distanceSqr(point, c);
            if (minDist === undefined || d < minDist) {
                minDist = d;
                idx = i;
            }
        }
        return idx !== undefined ? this._buildPoint(idx, result) : undefined;
    }
    /* Returns a typed array view containing the specied subsection of a single property channel */
    fetchChannel(property, offset = 0, count = undefined) {
        const vec = this._points[property];
        console.assert(vec !== undefined, `${property} is not a valid track point property`);
        // If no count is specified, grab all remaining elements (being careful to only fetch allocated elements)
        if (count === undefined)
            count = vec.length - offset;
        const VecType = vec.constructor;
        const res = new VecType(vec.buffer, offset * vec.BYTES_PER_ELEMENT, count);
        return res;
    }
    /* Returns a single value from the specified channel at idx */
    fetchProperty(property, idx) {
        const vec = this._points[property];
        console.assert(vec !== undefined, `${property} is not a valid track point property`);
        return idx < vec.length && idx >= 0 ? vec[idx] : undefined;
    }
    /* For each property of result, fill it from the corresponding channel at the given index */
    _buildPoint(idx, result) {
        const props = Object.keys(result);
        for (let i = 0; i < props.length; i++) {
            const channel = this._points[props[i]];
            if (channel)
                result[props[i]] = channel[idx];
            else if (props[i] === 'index')
                result.index = idx;
        }
        return result;
    }
    _assertQueryValid(vec, key) {
        if (vec === undefined)
            throw new Error(`${key} is not a Track property`);
        if (!this.isChannelSorted(key))
            throw new Error(`${key} is not a sorted property, so is not queryable`);
    }
}
export default class TrackCollection {
    constructor() {
        this._tracks = {};
    }
    onSceneLoad(scene, dispatch) {
        const defaultTarget = scene.sceneSettings.defaultTarget;
        if (defaultTarget) {
            const activeId = defaultTarget.activityId ? defaultTarget.activityId : defaultTarget;
            dispatch({ type: 'SET_ACTIVE_TRACK', trackId: activeId });
        }
    }
    addTrack(id, channels, pointCount) {
        this._tracks[id] = new Track(id, channels, pointCount);
        return this._tracks[id];
    }
    removeTrack(id) {
        delete this._tracks[id];
    }
    // @TODO: Rename to get()
    getTrack(id) {
        return this._tracks[id];
    }
    update(dispatch) {
        Object.keys(this._tracks).forEach(trackId => {
            this._tracks[trackId].clearDirty();
        });
        dispatch({
            type: 'SET_TRACKS',
            tracks: this._tracks,
        });
    }
}
export function tracksReducer(state = {
    activeTrack: undefined,
    tracks: {},
}, action = {}) {
    switch (action.type) {
        case 'SET_ACTIVE_TRACK': {
            const newState = Object.assign({}, state);
            newState.activeTrack = action.trackId;
            return newState;
        }
        case 'SET_TRACKS': {
            return Object.assign(Object.assign({}, state), { tracks: action.tracks });
        }
        default:
            return state;
    }
}
